import React, { useEffect, useState } from 'react';
import { supabase, api } from '../lib/api';
import { format, isPast } from 'date-fns';
import ClassPhoto from '../components/ClassPhoto';

const TREATMENT_PHOTOS = {
  rlt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/RLT%20photo.jpeg',
  cryo:             'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/cryo2.jpeg',
  'hbot-30':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-60':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-90':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'sauna-communal': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  'sauna-private':  'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  mat:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/Mat.jpeg',
  nmt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt.png',
  'nmt-assessment': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt1st%20session.png',
};


export default function BookingsPage() {
  // Wellness/treatment bookings (Supabase) — existing
  const [bookings, setBookings] = useState([]);
  // Class bookings (TeamUp) — new
  const [classBookings, setClassBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [cancelling, setCancelling] = useState(null);              // treatment booking id being cancelled
  const [cancellingClass, setCancellingClass] = useState(null);    // event id being cancelled

  useEffect(() => {
    // Fetch both sources in parallel; finish loading once both settle.
    Promise.allSettled([
      supabase.rpc('get_my_bookings')
        .then(({ data }) => setBookings(data || []))
        .catch(() => {}),
      api.getTeamupBookings()
        .then(d => setClassBookings(Array.isArray(d?.bookings) ? d.bookings : []))
        .catch(() => setClassBookings([])),
    ]).finally(() => setLoading(false));
  }, []);

  async function cancel(id) {
    if (!window.confirm('Cancel this booking? Points will be refunded if you have enough notice.')) return;
    setCancelling(id);
    try {
      await api.cancelBooking(id);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
    } catch (err) { alert(err.error || 'Could not cancel booking'); }
    setCancelling(null);
  }

  async function cancelClass(b) {
    if (!b?.eventId) return;
    const when = `${format(new Date(b.startsAt), "EEE d MMM 'at' h:mm aaa")}`;
    if (!window.confirm(`Cancel your ${b.name} booking on ${when}?`)) return;
    setCancellingClass(b.eventId);
    try {
      await api.cancelTeamupBooking(b.eventId);
      // Remove from the local list — refetch on next visit will reconcile
      setClassBookings(prev => prev.filter(x => x.eventId !== b.eventId));
    } catch (err) {
      alert(err?.error || 'Could not cancel — please try again or contact us.');
    }
    setCancellingClass(null);
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  // Existing treatment bookings: split into upcoming / past
  const upcomingTreatments = bookings.filter(b => b.status === 'confirmed' && b.availability_slots && !isPast(new Date(b.availability_slots.starts_at)));
  const pastTreatments     = bookings.filter(b => b.status !== 'confirmed' || (b.availability_slots && isPast(new Date(b.availability_slots.starts_at))));

  // Class bookings (already filtered server-side to upcoming only)
  const upcomingClasses = classBookings;

  // Merge upcoming items (treatments + classes) chronologically so members
  // see one timeline of "what's coming up next" instead of two separate lists.
  const upcomingItems = [
    ...upcomingTreatments.map(b => ({
      kind: 'treatment',
      sortDate: b.availability_slots?.starts_at,
      key: `tx-${b.id}`,
      data: b,
    })),
    ...upcomingClasses.map(b => ({
      kind: 'class',
      sortDate: b.startsAt,
      key: `cls-${b.attendanceId || b.eventId}`,
      data: b,
    })),
  ].sort((a, c) => {
    const at = a.sortDate ? new Date(a.sortDate).getTime() : Number.MAX_SAFE_INTEGER;
    const bt = c.sortDate ? new Date(c.sortDate).getTime() : Number.MAX_SAFE_INTEGER;
    return at - bt;
  });

  // Existing card — for treatments / biohack
  function BookingCard({ b }) {
    const past = b.status === 'cancelled' || b.status === 'completed' || (b.availability_slots && isPast(new Date(b.availability_slots.starts_at)));
    return (
      <div className="card" style={{ padding: 16, marginBottom: 10, opacity: past ? 0.65 : 1 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
            background: TREATMENT_PHOTOS[b.treatments?.slug] ? 'none' : '#1A1A1A',
            backgroundImage: TREATMENT_PHOTOS[b.treatments?.slug] ? `url(${TREATMENT_PHOTOS[b.treatments?.slug]})` : 'none',
            backgroundSize: 'cover', backgroundPosition: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {!TREATMENT_PHOTOS[b.treatments?.slug] && <span style={{ fontSize: '1.4rem' }}>💚</span>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>{b.treatments?.name}</div>
            {b.availability_slots && (
              <div style={{ color: 'var(--grey-500)', fontSize: '0.82rem', marginTop: 2 }}>
                {format(new Date(b.availability_slots.starts_at), "EEE d MMM 'at' h:mm aaa")}
              </div>
            )}
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                background: b.status === 'confirmed' ? 'var(--green-pale)' : 'var(--grey-100)',
                color: b.status === 'confirmed' ? 'var(--green)' : 'var(--grey-700)',
              }}>
                {b.status.toUpperCase()}
              </span>
              {b.payment_method === 'points'
                ? <span className="points-badge" style={{ fontSize: '0.7rem' }}>{b.points_cost} pts</span>
                : <span style={{ fontSize: '0.7rem', color: 'var(--grey-500)' }}>£{(b.payg_price_pence / 100).toFixed(2)}</span>
              }
            </div>
          </div>
          {b.status === 'confirmed' && !past && (
            <button
              onClick={() => cancel(b.id)}
              disabled={cancelling === b.id}
              style={{ fontSize: '0.75rem', color: 'var(--danger)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
            >
              {cancelling === b.id ? '…' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // New card — for TeamUp class bookings
  function ClassCard({ b }) {
    const isCancellingNow = cancellingClass === b.eventId;
    return (
      <div className="card" style={{ padding: 16, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <ClassPhoto name={b.name} width={52} height={52} radius={10} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>{b.name}</div>
            {b.startsAt && (
              <div style={{ color: 'var(--grey-500)', fontSize: '0.82rem', marginTop: 2 }}>
                {format(new Date(b.startsAt), "EEE d MMM 'at' h:mm aaa")}
                {b.instructor ? ` · ${b.instructor}` : ''}
              </div>
            )}
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                background: 'var(--green-pale)', color: 'var(--green)',
              }}>
                CONFIRMED
              </span>
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                background: 'var(--grey-100)', color: 'var(--grey-700)',
              }}>
                CLASS
              </span>
            </div>
          </div>
          <button
            onClick={() => cancelClass(b)}
            disabled={isCancellingNow || cancellingClass !== null}
            style={{ fontSize: '0.75rem', color: 'var(--danger)', fontWeight: 600, background: 'none', border: 'none', cursor: isCancellingNow ? 'default' : 'pointer', flexShrink: 0, opacity: isCancellingNow ? 0.6 : 1 }}
          >
            {isCancellingNow ? '…' : 'Cancel'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">My Bookings</h1>
      {upcomingItems.length === 0 && pastTreatments.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 48 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>📅</div>
          No bookings yet
        </div>
      )}
      {upcomingItems.length > 0 && (
        <>
          <div className="section-title">Upcoming</div>
          {upcomingItems.map(item =>
            item.kind === 'treatment'
              ? <BookingCard key={item.key} b={item.data} />
              : <ClassCard   key={item.key} b={item.data} />
          )}
        </>
      )}
      {pastTreatments.length > 0 && (
        <>
          <div className="section-title">Past</div>
          {pastTreatments.map(b => <BookingCard key={b.id} b={b} />)}
        </>
      )}
    </div>
  );
}
